/**
 * AdminRoute Component
 *
 * A route guard that restricts access to admin-only pages.
 * - Redirects unauthenticated users to home page
 * - Redirects authenticated non-admin users to home page
 * - Renders children only for authenticated admin users
 *
 * Note: This component fetches fresh user data from the backend to ensure
 * the admin status is current (handles cases where admin was granted after login).
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getProfile } from '../api/auth';
import { LoadingSpinner } from './ui/LoadingSpinner';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, user, updateUser } = useAuthStore();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    async function checkAdminStatus() {
      // Not logged in - no need to check
      if (!isAuthenticated || !user) {
        setIsChecking(false);
        setIsAdmin(false);
        return;
      }

      try {
        // Fetch fresh user data from backend to get current admin status
        const freshUser = await getProfile();

        // Update the local user data if admin status changed
        if (freshUser.is_admin !== user.is_admin) {
          updateUser({ is_admin: freshUser.is_admin });
        }

        setIsAdmin(freshUser.is_admin === true);
      } catch (error) {
        console.error('[AdminRoute] Failed to verify admin status:', error);
        // Fall back to cached value on error
        setIsAdmin(user.is_admin === true);
      } finally {
        setIsChecking(false);
      }
    }

    checkAdminStatus();
  }, [isAuthenticated, user, updateUser]);

  // Show loading while checking admin status
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-neutral-500">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not logged in - redirect to home
  if (!isAuthenticated || !user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Logged in but not admin - redirect to home
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and is an admin - render the protected content
  return <>{children}</>;
}
